import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
if (typeof document !== 'undefined') {
  const l = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null
  if (l) l.href = '/manifest-employee.json'
  document.title = 'Mon Planning — Connexion'
  const m = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
  if (m) m.content = '#1e40af'
}
import axios from 'axios'
import { setEmployeeToken, setEmployeeData } from '../../lib/employeeApi'
import { registerFcmToken } from '../../lib/useFcm'
import { isKiosk } from '../../lib/kiosk'

const BASE = `${import.meta.env.VITE_API_URL || ''}/api`
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back']

interface Estab { id: string; name: string }
interface Person { id: string; firstName: string; lastName: string }

// Connexion employé (27/09/2026) : magasin → prénom dans la liste → code à 4 chiffres.
// Plus d'adresse e-mail. Le magasin et le prénom choisis sont retenus sur le téléphone.
export default function MobileLogin() {
  const nav = useNavigate()
  const [estabs, setEstabs] = useState<Estab[]>([])
  const [estabId, setEstabId] = useState(() => localStorage.getItem('loginEstablishmentId') || '')
  const [people, setPeople] = useState<Person[]>([])
  // Tablette du magasin : le prénom n'est jamais pré-rempli (chacun choisit le sien)
  const kiosk = isKiosk()
  const [employeeId, setEmployeeId] = useState(() => (kiosk ? '' : localStorage.getItem('loginEmployeeId') || ''))
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Magasins (un seul → choisi d'office)
  useEffect(() => {
    axios.get(`${BASE}/employee/auth/establishments`).then(r => {
      const list: Estab[] = r.data || []
      setEstabs(list)
      if (list.length === 1) setEstabId(list[0].id)
      else if (estabId && !list.some(e => e.id === estabId)) setEstabId('')
    }).catch(() => setError('Serveur injoignable, réessayez'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Personnel du magasin choisi
  useEffect(() => {
    if (!estabId) { setPeople([]); return }
    axios.get(`${BASE}/employee/auth/employees`, { params: { establishmentId: estabId } }).then(r => {
      const list: Person[] = r.data || []
      setPeople(list)
      if (employeeId && !list.some(p => p.id === employeeId)) setEmployeeId('')
    }).catch(() => setPeople([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estabId])

  useEffect(() => {
    if (pin.length === 4) submitLogin(pin)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin])

  // Prénom seul, ou prénom + initiale quand deux personnes ont le même prénom
  const label = (p: Person) => {
    const same = people.filter(x => x.firstName.trim().toLowerCase() === p.firstName.trim().toLowerCase()).length > 1
    const initial = (p.lastName || '').trim().charAt(0)
    return same && initial ? `${p.firstName} ${initial}.` : p.firstName
  }

  function pressKey(k: string) {
    if (loading) return
    if (!employeeId) { setError('Choisissez d’abord votre prénom'); return }
    if (k === 'back') { setPin(p => p.slice(0, -1)); return }
    if (pin.length >= 4) return
    setError('')
    setPin(pin + k)
  }

  async function submitLogin(currentPin: string) {
    if (!estabId || !employeeId) { setError('Choisissez votre magasin et votre prénom'); setPin(''); return }
    setLoading(true)
    setError('')
    try {
      const res = await axios.post(`${BASE}/employee/auth/login`, { establishmentId: estabId, employeeId, pin: currentPin })
      localStorage.setItem('loginEstablishmentId', estabId)
      if (!kiosk) localStorage.setItem('loginEmployeeId', employeeId)
      setEmployeeToken(res.data.token)
      setEmployeeData(res.data.employee)
      registerFcmToken()
      nav('/app/home')
    } catch {
      setError('Code incorrect')
      setPin('')
    } finally {
      setLoading(false)
    }
  }

  const select = 'w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-800 text-base bg-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100'

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-600 to-blue-800 flex flex-col items-center justify-center p-6">
      <div className="mb-6 text-center">
        <div className="text-white text-3xl font-bold">Utile</div>
        <div className="text-blue-200 text-sm mt-1">{kiosk ? 'Tablette du magasin' : 'Espace Salarié'}</div>
      </div>

      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-5">
        {estabs.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Magasin</label>
            <select value={estabId} onChange={e => { setEstabId(e.target.value); setEmployeeId(''); setPin(''); setError('') }} className={select}>
              <option value="">— Choisir —</option>
              {estabs.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
          <select value={employeeId} disabled={!estabId}
            onChange={e => { setEmployeeId(e.target.value); setPin(''); setError('') }}
            className={`${select} disabled:bg-gray-50 disabled:text-gray-400`}>
            <option value="">{estabId ? '— Choisir votre prénom —' : 'Choisissez d’abord le magasin'}</option>
            {people.map(p => <option key={p.id} value={p.id}>{label(p)}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Code</label>
          <div className="flex justify-center gap-4 mb-4">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${i < pin.length ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`} />
            ))}
          </div>

          {error && <p className="text-red-500 text-sm text-center mb-3">{error}</p>}

          <div className="grid grid-cols-3 gap-3">
            {KEYS.map((k, i) => {
              if (k === '') return <div key={i} />
              return (
                <button key={i} type="button" onClick={() => pressKey(k)} disabled={loading}
                  className={`h-14 rounded-xl text-xl font-medium transition-all active:scale-95 ${
                    k === 'back'
                      ? 'text-gray-500 bg-gray-100 hover:bg-gray-200'
                      : 'text-gray-800 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 border border-gray-200'
                  }`}>
                  {k === 'back' ? '⌫' : k}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
